import React, { useEffect, useRef, useState } from "react";
import Config from "../utils/Config";

export default function DashboardSupportForm({ initialRequest = null }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const request = useRef(null);
  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    // Navigation may prefill an empty form, but must never replace an existing draft.
    if (!initialRequest || subject || message || request.current) return;
    setSubject(initialRequest.subject || "");
    setMessage(initialRequest.message || "");
  }, [initialRequest, subject, message]);

  const submit = async event => {
    event.preventDefault();
    if (request.current) return;
    if (!subject.trim() || !message.trim() || subject.length > 150 || message.length > 2000) {
      setFeedback({ error: true, text: "Enter a subject (up to 150 characters) and message (up to 2,000 characters)." });
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setFeedback(null);
    try {
      const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      if (!token) {
        setFeedback({ error: true, text: "Please sign in again before sending your support request." });
        return;
      }
      const response = await fetch(Config.API.API_BASE_URL + "/api/support/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ subject: subject.trim(), message: message.trim() }),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => null);
      if (controller.signal.aborted) return;
      if (response.status === 201 && typeof data?.requestId === "string" && data.requestId.trim()) {
        setFeedback({ error: false, text: "Support request saved. Reference: " + data.requestId });
        setSubject("");
        setMessage("");
      } else {
        let text;
        if (response.status === 429) {
          const seconds = Number(data?.retryAfterSeconds || response.headers.get("Retry-After"));
          text = "Support submission limit or duplicate request. " + (Number.isFinite(seconds) && seconds > 0
            ? "Try again in " + Math.ceil(seconds / 60) + " minute(s)."
            : "Please wait before retrying.");
        } else if (response.status === 400) {
          text = typeof data?.message === "string" ? data.message : "Check your subject and message and try again.";
        } else if (response.status === 401 || response.status === 403) {
          text = "Please sign in again before sending your support request.";
        } else {
          text = "Your support request could not be confirmed as saved. Please try again later.";
        }
        setFeedback({ error: true, text });
      }
    } catch {
      if (!controller.signal.aborted) setFeedback({ error: true, text: "Unable to connect. Your request may not have been saved. Check your connection before retrying." });
    } finally {
      if (!controller.signal.aborted) {
        request.current = null;
        setPending(false);
      }
    }
  };

  return <div className="do-support-form">
    {feedback && <p role={feedback.error ? "alert" : "status"}>{feedback.text}</p>}
    <form id="dashboard-support-form" onSubmit={submit} noValidate aria-busy={pending}>
      <label htmlFor="dashboard-support-subject">Subject</label>
      <input id="dashboard-support-subject" name="subject" type="text" maxLength={150} required value={subject} disabled={pending} onChange={event => setSubject(event.target.value)} />
      <label htmlFor="dashboard-support-message">Message</label>
      <textarea id="dashboard-support-message" name="message" rows={5} maxLength={2000} required value={message} disabled={pending} onChange={event => setMessage(event.target.value)} />
      <div className="do-support-form-actions">
        <button type="submit" className="do-support-link" disabled={pending}>{pending ? "Sending…" : "Send Message"}</button>
      </div>
    </form>
  </div>;
}
